/**
 * One tile's data.
 *
 * Goes through the app's TanStack query client rather than `report-kit`'s own
 * `useReportQuery`, which is a hand-rolled hook with no cache and no retry.
 * That is the right shape for a builder previewing ONE report; a dashboard of
 * twelve tiles wants the shared cache, because the deduplication is the whole
 * difference between twelve requests and the four distinct questions they
 * actually ask.
 *
 * KEYED ON THE PLAN, not on the tile. `planKey` covers the model, the
 * group-bys, the measures, the filters and the window, and deliberately
 * excludes presentation — so switching a tile from a table to a bar reuses the
 * answer instead of re-running the aggregate, while changing a filter does not.
 * Neither the dashboard id nor the tile id is in the key, so the same report
 * placed twice collapses to one request.
 */
import type { QueryPlan, QueryResult, ReportModel, ReportSpec } from '@clockwise/semantic-core';
import { planKey, planQuery, validateSpec } from '@clockwise/semantic-core';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { analyticsApi } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';

export interface TileQuery {
  result?: QueryResult;
  loading: boolean;
  /** A sentence to show in the tile. Never a code. */
  error?: string;
  refetch: () => void;
  /**
   * The plan this tile ran, for the renderer to project by.
   *
   * Built here already — it is what keys the cache — and returned because
   * `ReportView` needs it: without one it projects with neither the memory
   * SORT nor the memory CAP, so a dashboard showed a different row order and a
   * different row count from the builder for the same saved report. Null until
   * the spec is runnable.
   */
  plan: QueryPlan | null;
}

export function useTileQuery(spec: ReportSpec | null, model: ReportModel | null): TileQuery {
  // GUARDED BEFORE IT IS SENT. The server refuses an unrunnable spec with the
  // same sentence, but a tile that can never succeed should say why rather
  // than spend a round trip discovering it — and a dashboard of twelve would
  // spend twelve.
  const verdict = useMemo(() => (spec && model ? validateSpec(model, spec) : null), [spec, model]);
  const runnable = spec !== null && model !== null && verdict?.ok === true;

  const plan = useMemo(
    () => (runnable && spec && model ? planQuery(model, spec) : null),
    [runnable, spec, model],
  );

  const query = useQuery({
    queryKey: queryKeys.dashboards.tile(plan ? planKey(plan) : 'none'),
    queryFn: ({ signal }) => {
      if (!spec) throw new Error('no spec');
      return analyticsApi.query(spec, signal);
    },
    enabled: runnable,
    // A dashboard is read repeatedly in one sitting — opened, filtered, filtered
    // back. Holding an answer for a minute makes the second look instant, and
    // an aggregate over a closed period does not change underneath a reader in
    // that window.
    staleTime: 60_000,
  });

  return {
    result: query.data,
    loading: query.isPending && runnable,
    error:
      verdict && !verdict.ok
        ? // The guard's own sentence, which names the fix.
          verdict.reason
        : query.error
          ? messageOf(query.error)
          : undefined,
    refetch: () => void query.refetch(),
    plan,
  };
}

function messageOf(err: unknown): string {
  return err instanceof Error && err.message ? err.message : 'This tile could not be loaded.';
}
